import { useState, useMemo } from 'react';
import { format, subDays, addDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Search,
  X,
  ArrowDownCircle,
  ArrowUpCircle,
  Loader2,
  Filter,
  AlertTriangle,
  Check,
} from 'lucide-react';
import { getFinancialStatus } from '@/utils/financialStatus';
import { toLocalDate } from '@/utils/dateHelpers';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BankTransaction } from '@/hooks/useBankTransactions';
import { useSearchReceivables, SearchableReceivable } from '@/hooks/useSearchReceivables';
import { useFinancialAccounts } from '@/hooks/useFinancialAccounts';
import { usePayablePaymentCharges } from '@/hooks/usePayablePaymentCharges';
import { useReceivablePaymentCharges } from '@/hooks/useReceivablePaymentCharges';
import { cn } from '@/lib/utils';

interface SearchReceivablesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: BankTransaction | null;
  onReconcile: (receivables: SearchableReceivable[]) => void;
  onReviewValues: (receivables: SearchableReceivable[]) => void;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

const getStatusBadge = (receivable: { status: string; due_date: string; total_value: number; paid_value: number; type?: 'receivable' | 'payable' }) => {
  const statusInfo = getFinancialStatus(receivable, receivable.type === 'payable' ? 'payable' : 'receivable');
  return <Badge variant={statusInfo.variant} className={statusInfo.badgeClassName || undefined}>{statusInfo.label}</Badge>;
};

export const SearchReceivablesDialog = ({
  open,
  onOpenChange,
  transaction,
  onReconcile,
  onReviewValues,
}: SearchReceivablesDialogProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'paid' | 'overdue'>('all');
  const [accountFilter, setAccountFilter] = useState<string>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);

  // Período padrão: 30 dias antes e depois da transação
  const txDate = transaction ? new Date(`${transaction.transaction_date}T12:00:00`) : new Date();
  const defaultStartDate = format(subDays(txDate, 30), 'yyyy-MM-dd');
  const defaultEndDate = format(txDate, 'yyyy-MM-dd');

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);

  // R6: direção travada pela linha do banco — universo E seleção
  const expectedType = transaction?.transaction_type === 'credit' ? 'receivable' : 'payable';

  const hasTerm = searchTerm.trim().length >= 2;
  const r2Window = {
    startDate: format(subDays(txDate, 5), 'yyyy-MM-dd'),
    endDate: format(addDays(txDate, 5), 'yyyy-MM-dd'),
  };

  const { accounts } = useFinancialAccounts();
  // Sem termo (a): títulos em aberto, vencimento na janela R2
  const { data: openDefault = [], isLoading: loadingOpen } = useSearchReceivables({
    type: expectedType,
    status: 'pending',
    ...r2Window,
    enabled: open && !!transaction && !hasTerm,
  });
  // Sem termo (b): baixados com payment_date na janela, conta da linha OU sem conta (R3/R4/R5)
  const { data: settledDefault = [], isLoading: loadingSettled } = useSearchReceivables({
    type: expectedType,
    status: 'paid',
    dateField: 'payment_date',
    ...r2Window,
    accountOrNull: transaction?.financial_account_id,
    enabled: open && !!transaction && !hasTerm,
  });
  // Com termo: busca livre como hoje (filtros manuais valem aqui)
  const { data: searched = [], isLoading: loadingSearch } = useSearchReceivables({
    startDate,
    endDate,
    searchTerm: searchTerm || undefined,
    financialAccountId: accountFilter !== 'all' ? accountFilter : undefined,
    type: expectedType,
    status: statusFilter,
    enabled: open && !!transaction && hasTerm,
  });

  const receivables = hasTerm ? searched : [...openDefault, ...settledDefault];
  const isLoading = hasTerm ? loadingSearch : (loadingOpen || loadingSettled);

  // D8b: encargos em lote dos títulos listados (direção única — R6)
  const listedIds = receivables.map((r) => r.id);
  const { data: recvCharges } = useReceivablePaymentCharges(expectedType === 'receivable' ? listedIds : []);
  const { data: payCharges } = usePayablePaymentCharges(expectedType === 'payable' ? listedIds : []);
  const chargesByTitle = (expectedType === 'receivable' ? recvCharges : payCharges) ?? {};

  // F-FIN P2: saldo = balance do banco (total − pago − desconto); fallback só se a coluna não vier
  const balanceOf = (r: SearchableReceivable) =>
    r.balance ?? r.total_value - (r.paid_value || 0);
  const isSettledRow = (r: SearchableReceivable) =>
    r.status === 'paid' || balanceOf(r) <= 0.009;
  // i.5: valor efetivo de caixa do título baixado
  const effectiveOf = (r: SearchableReceivable) => {
    const c = chargesByTitle[r.id];
    return (r.paid_value || 0) + (c?.totalInterest ?? 0) + (c?.totalFine ?? 0) - (c?.totalDiscount ?? 0);
  };
  const rowValue = (r: SearchableReceivable) =>
    isSettledRow(r) ? effectiveOf(r) : balanceOf(r);

  // Filtrar para mostrar o tipo correto primeiro
  const sortedReceivables = useMemo(() => {
    return [...receivables].sort((a, b) => {
      // Priorizar o tipo esperado
      if (a.type === expectedType && b.type !== expectedType) return -1;
      if (a.type !== expectedType && b.type === expectedType) return 1;
      // Depois ordenar por valor mais próximo
      const bankValue = Math.abs(transaction?.value || 0);
      const diffA = Math.abs(rowValue(a) - bankValue);
      const diffB = Math.abs(rowValue(b) - bankValue);
      return diffA - diffB;
    });
  }, [receivables, expectedType, transaction?.value]);

  // Calcular valores selecionados
  const selectedReceivables = sortedReceivables.filter(r => selectedIds.has(r.id));
  // D8a: aberto soma pelo saldo; baixado soma pelo efetivo (i.5)
  const selectedTotal = selectedReceivables.reduce((sum, r) => sum + rowValue(r), 0);
  const bankValue = Math.abs(transaction?.value || 0);
  const difference = bankValue - selectedTotal;

  const handleToggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === sortedReceivables.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedReceivables.map(r => r.id)));
    }
  };

  const handleReconcile = () => {
    if (selectedReceivables.length === 0) return;
    
    if (Math.abs(difference) < 0.01) {
      // Valores batem, conciliar direto
      onReconcile(selectedReceivables);
    } else {
      // Há diferença, ir para revisão de valores
      onReviewValues(selectedReceivables);
    }
  };

  const handleClose = () => {
    setSelectedIds(new Set());
    setSearchTerm('');
    setStatusFilter('all');
    setAccountFilter('all');
    setShowFilters(false);
    onOpenChange(false);
  };

  if (!transaction) return null;

  const isCredit = transaction.transaction_type === 'credit';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Buscar Lançamento
          </DialogTitle>
        </DialogHeader>

        {/* Card da transação bancária */}
        <div className={cn(
          'mx-6 p-4 rounded-lg border',
          isCredit ? 'bg-green-500/5 border-green-500/20' : 'bg-red-500/5 border-red-500/20'
        )}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={cn(
                'p-2 rounded-full',
                isCredit ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'
              )}>
                {isCredit ? <ArrowDownCircle className="h-5 w-5" /> : <ArrowUpCircle className="h-5 w-5" />}
              </div>
              <div>
                <p className="font-medium">{transaction.description}</p>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(`${transaction.transaction_date}T12:00:00`), 'dd/MM/yyyy', { locale: ptBR })}
                  {transaction.counterpart_name && ` • ${transaction.counterpart_name}`}
                </p>
              </div>
            </div>
            <p className={cn('text-xl font-bold', isCredit ? 'text-green-600' : 'text-red-600')}>
              {isCredit ? '+' : '-'}{formatCurrency(bankValue)}
            </p>
          </div>
        </div>

        {/* Filtros */}
        <div className="px-6 py-4 space-y-4 border-b">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Pesquisar por descrição, número..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button
              variant={showFilters ? 'secondary' : 'outline'}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtros
            </Button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-1">
                <Label className="text-xs">Data Inicial</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Data Final</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Situação</Label>
                <Select value={statusFilter} onValueChange={(v: any) => setStatusFilter(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="pending">Pendente</SelectItem>
                    <SelectItem value="paid">Pago</SelectItem>
                    <SelectItem value="overdue">Vencido</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Conta</Label>
                <Select value={accountFilter} onValueChange={setAccountFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {accounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>{acc.account_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {isCredit
              ? <ArrowDownCircle className="h-3 w-3 text-green-600" />
              : <ArrowUpCircle className="h-3 w-3 text-red-600" />}
            <span>{isCredit ? 'Recebimentos' : 'Pagamentos'} — direção definida pela linha do banco</span>
          </div>
        </div>

        {/* Lista de lançamentos */}
        <ScrollArea className="flex-1 [&>[data-radix-scroll-area-viewport]>div]:!block">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : sortedReceivables.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
              <Search className="h-8 w-8 mb-2 opacity-50" />
              <p>Nenhum lançamento encontrado</p>
              <p className="text-sm">Tente ajustar os filtros</p>
            </div>
          ) : (
            <div className="divide-y">
              {/* Header da tabela */}
              <div className="px-6 py-3 bg-muted/50 grid grid-cols-[2rem_6rem_6rem_minmax(0,1fr)_6rem_8rem] items-center gap-2 text-sm font-medium text-muted-foreground sticky top-0">
                <div>
                  <Checkbox
                    checked={selectedIds.size === sortedReceivables.length && sortedReceivables.length > 0}
                    onCheckedChange={handleSelectAll}
                  />
                </div>
                <div>Vencimento</div>
                <div>{expectedType === 'payable' ? 'Pago' : 'Recebido'}</div>
                <div className="min-w-0">Descrição</div>
                <div>Situação</div>
                <div className="text-right">Valor</div>
              </div>
              
              {sortedReceivables.map((receivable) => {
                const isSelected = selectedIds.has(receivable.id);
                const remainingValue = balanceOf(receivable);
                const isExactMatch = Math.abs(rowValue(receivable) - bankValue) < 0.01;
                const isReceivable = receivable.type === 'receivable';

                return (
                  <div
                    key={receivable.id}
                    onClick={() => handleToggleSelect(receivable.id)}
                    className={cn(
                      'px-6 py-3 grid grid-cols-[2rem_6rem_6rem_minmax(0,1fr)_6rem_8rem] items-center gap-2 cursor-pointer transition-colors',
                      isSelected ? 'bg-primary/5' : 'hover:bg-muted/50',
                      isExactMatch && 'ring-2 ring-inset ring-green-500/20'
                    )}
                  >
                    <div>
                      <Checkbox checked={isSelected} />
                    </div>
                    <div className="text-sm">
                      {format(toLocalDate(receivable.due_date), 'dd/MM/yyyy', { locale: ptBR })}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {receivable.payment_date
                        ? format(toLocalDate(receivable.payment_date), 'dd/MM/yyyy', { locale: ptBR })
                        : '-'
                      }
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={cn(
                          'p-1 rounded-full shrink-0',
                          isReceivable ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'
                        )}>
                          {isReceivable ? <ArrowDownCircle className="h-3 w-3" /> : <ArrowUpCircle className="h-3 w-3" />}
                        </div>
                        <span className="font-medium truncate" title={receivable.description}>{receivable.description}</span>
                        {isExactMatch && (
                          <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs shrink-0">
                            Valor exato
                          </Badge>
                        )}
                      </div>
                      <p
                        className="text-sm text-muted-foreground truncate"
                        title={`${receivable.customer_name || receivable.supplier_name || ''}${receivable.category_name ? ` • ${receivable.category_name}` : ''}`}
                      >
                        {receivable.customer_name || receivable.supplier_name}
                        {receivable.category_name && ` • ${receivable.category_name}`}
                      </p>
                    </div>
                    <div>
                      {getStatusBadge(receivable)}
                    </div>
                    <div className="text-right">
                      {(() => {
                        const isSettled = receivable.status === 'paid' || remainingValue <= 0.009;
                        const c = chargesByTitle[receivable.id];
                        const hasCharges = !!c && (c.totalInterest > 0 || c.totalFine > 0 || c.totalDiscount > 0);
                        return (
                          <>
                            <p className={cn('font-bold tabular-nums', isReceivable ? 'text-green-600' : 'text-red-600')}>
                              {formatCurrency(isSettled ? effectiveOf(receivable) : remainingValue)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {isSettled
                                ? hasCharges
                                  ? `${formatCurrency(receivable.paid_value || 0)}${c!.totalInterest > 0 ? ` + ${formatCurrency(c!.totalInterest)} juros` : ''}${c!.totalFine > 0 ? ` + ${formatCurrency(c!.totalFine)} multa` : ''}${c!.totalDiscount > 0 ? ` - ${formatCurrency(c!.totalDiscount)} desc.` : ''}`
                                  : (expectedType === 'payable' ? 'pago' : 'recebido')
                                : (receivable.paid_value || 0) > 0
                                  ? `Total: ${formatCurrency(receivable.total_value)}`
                                  : null}
                            </p>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Footer com totais */}
        <div className="border-t p-4 bg-muted/30">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
              <div>
                <span className="text-muted-foreground">Valor do banco:</span>
                <span className="ml-2 font-bold">{formatCurrency(bankValue)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Selecionado ({selectedReceivables.length}):</span>
                <span className="ml-2 font-bold">{formatCurrency(selectedTotal)}</span>
              </div>
              <div className={cn(
                'flex items-center gap-1',
                Math.abs(difference) < 0.01 ? 'text-green-600' : 'text-amber-600'
              )}>
                {Math.abs(difference) < 0.01 ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <AlertTriangle className="h-4 w-4" />
                )}
                <span className="text-muted-foreground">Diferença:</span>
                <span className="font-bold">
                  {difference > 0 ? '+' : ''}{formatCurrency(difference)}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {selectedReceivables.length > 0 && Math.abs(difference) > 0.01 && (
                <Button variant="outline" onClick={() => onReviewValues(selectedReceivables)}>
                  Revisar valores ({selectedReceivables.length})
                </Button>
              )}
              <Button variant="outline" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                onClick={handleReconcile}
                disabled={selectedReceivables.length === 0}
              >
                {Math.abs(difference) < 0.01 ? 'Conciliar' : 'Revisar e Conciliar'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
